import { resolveCurrencyImage } from "@/helpers/currencyImage"
import { compareCurrencyCodes } from "@/helpers/currencyPresentation"
import { resolveItemImage } from "@/helpers/itemImage"
import { buildScaleIndex, formatMoneyRange } from "@/helpers/money"
import {
  buildStatAmountRows,
  formatAmount,
  formatStatRange
} from "@/helpers/statFormat"
import {
  IAttackView,
  IAttackViewInput,
  IEngagementOpenStep,
  IMobCardDoll,
  IMobLootTile,
  IMobStatGroups,
  IRefusalContext
} from "@/hooks/useMobCard/types"
import { IStandingCell } from "@/hooks/usePlayerStanding/types"
import {
  BATTLE_DENIAL_REASON,
  BATTLE_STATE,
  IBattleView
} from "@/store/apis/battleAPI/battle.types"
import {
  ICurrencyEntry,
  IEquipmentSlot,
  IStatEntry,
  IStatGroup
} from "@/store/apis/catalogsAPI/catalogs.types"
import { IAttackRefusal } from "@/store/apis/mobsAPI/attackRefusal"
import { IKindAvailability } from "@/store/apis/mobsAPI/cellAvailability"
import {
  IMobCard,
  IMobDollItem,
  IMobLootRow,
  IMobStatValue,
  MOB_ACTION_DENIAL_REASON
} from "@/store/apis/mobsAPI/mobs.types"
import { IVitalView } from "@/types/vitals.types"

export const MAX_HP_STAT = "max_hp"
export const MAX_MANA_STAT = "max_mana"

const VITAL_BARS: readonly { statCode: string; code: string }[] = [
  { statCode: MAX_HP_STAT, code: "hp" },
  { statCode: MAX_MANA_STAT, code: "mana" }
]

export const buildMobVitals = (stats: IMobStatValue[]): IVitalView[] =>
  VITAL_BARS.flatMap(({ statCode, code }): IVitalView[] => {
    const stat = stats.find((row) => row.statCode === statCode)
    return stat ? [{ code, current: stat.value, max: stat.value, fill: 1 }] : []
  })

export const buildMobDoll = (
  doll: IMobDollItem[],
  slotLayout: IEquipmentSlot[]
): IMobCardDoll => ({
  layout: slotLayout.map((slot) => slot.position),
  itemsByPosition: Object.fromEntries(
    doll.map((row) => [
      row.position,
      {
        definitionCode: row.itemCode,
        rarityCode: row.rarityCode,
        hasRarityDimension: true,
        inactive: false
      }
    ])
  )
})

export const lootWithoutDoll = (
  loot: IMobLootRow[],
  doll: IMobDollItem[]
): IMobLootRow[] =>
  loot.filter(
    (row) =>
      !doll.some(
        (item) =>
          item.itemCode === row.itemCode && item.rarityCode === row.rarityCode
      )
  )

export const formatQuantityRange = (min: number, max: number): string =>
  min === max ? formatAmount(min) : formatStatRange(min, max)

export const buildLootStrip = (
  card: IMobCard,
  currencies: readonly ICurrencyEntry[],
  locale: string,
  order: readonly string[]
): IMobLootTile[] => {
  const scales = buildScaleIndex(currencies)
  const currencyTiles = card.currencyLoot
    .slice()
    .sort((a, b) => compareCurrencyCodes(a.currencyCode, b.currencyCode, order))
    .flatMap((row): IMobLootTile[] => {
      const scale = scales.get(row.currencyCode)
      const rangeText =
        scale === undefined
          ? null
          : formatMoneyRange(row.amountMin, row.amountMax, scale, locale)
      return rangeText === null
        ? []
        : [
            {
              kind: "currency",
              currencyCode: row.currencyCode,
              rangeText,
              iconUrl: resolveCurrencyImage(row.currencyCode)
            }
          ]
    })
  const itemTiles = lootWithoutDoll(card.loot, card.doll).map(
    (row): IMobLootTile => ({
      kind: "item",
      itemCode: row.itemCode,
      rarityCode: row.rarityCode,
      quantityText: formatQuantityRange(row.quantityMin, row.quantityMax),
      iconUrl: resolveItemImage(row.itemCode)
    })
  )
  return [...currencyTiles, ...itemTiles]
}

export const showsLoot = (strip: readonly IMobLootTile[]): boolean =>
  strip.length > 0

export const groupMobStats = (
  stats: IMobStatValue[],
  statCatalog: IStatEntry[]
): IMobStatGroups => {
  const groupOf = new Map(statCatalog.map((entry) => [entry.code, entry.group]))
  const rowsOf = (group: IStatGroup) =>
    buildStatAmountRows(
      stats
        .filter((stat) => groupOf.get(stat.statCode) === group)
        .map(({ statCode, value }) => ({ code: statCode, value })),
      statCatalog
    )
  return {
    attributes: rowsOf("ATTRIBUTE"),
    modifiers: rowsOf("MODIFIER"),
    derived: rowsOf("DERIVED")
  }
}

const REFUSAL_HINT: Record<IAttackRefusal, string> = {
  [MOB_ACTION_DENIAL_REASON.PLAYER_MOVING]: "hint.stopToAttack",
  [MOB_ACTION_DENIAL_REASON.NOT_ON_CELL]: "hint.notOnCell",
  [MOB_ACTION_DENIAL_REASON.NO_FREE_MOB]: "hint.someoneFaster",
  [BATTLE_DENIAL_REASON.HP_TOO_LOW]: "hint.hpTooLow",
  FAILED: "hint.failed"
}

/** Latches false for the card's lifetime once its tapped mob is seen out of its fight. */
export const nextTappedMobFighting = (
  previous: boolean,
  fromFight: boolean,
  mobFighting: boolean
): boolean => previous && fromFight && mobFighting

const AVAILABILITY_HINT: Partial<Record<IKindAvailability, string>> = {
  taken: "hint.mobFighting",
  gone: "hint.mobGone"
}

const isBlockingRefusal = (
  refusal: IAttackRefusal | null
): refusal is Exclude<IAttackRefusal, "FAILED"> =>
  refusal !== null && refusal !== "FAILED"

const attackHint = (input: IAttackViewInput): string | null => {
  if (input.busy) {
    return null
  }
  if (input.tappedMobFighting) {
    return "hint.mobFighting"
  }
  if (input.isMoving) {
    return "hint.stopToAttack"
  }
  if (!input.onCardCell) {
    return "hint.notOnCell"
  }
  if (input.hpTooLow || input.refusal === BATTLE_DENIAL_REASON.HP_TOO_LOW) {
    return REFUSAL_HINT[BATTLE_DENIAL_REASON.HP_TOO_LOW]
  }
  if (isBlockingRefusal(input.refusal)) {
    return REFUSAL_HINT[input.refusal]
  }
  const liveHint = AVAILABILITY_HINT[input.availability]
  if (liveHint !== undefined) {
    return liveHint
  }
  return input.refusal === "FAILED" ? REFUSAL_HINT.FAILED : null
}

export const deriveAttackView = (input: IAttackViewInput): IAttackView => {
  if (input.staleFight) {
    return {
      kind: "staleFight",
      busy: input.busy,
      hintKey:
        !input.busy && input.refusal === "FAILED" ? REFUSAL_HINT.FAILED : null
    }
  }
  return {
    kind: "attack",
    enabled:
      !input.isMoving &&
      input.onCardCell &&
      !input.tappedMobFighting &&
      !input.busy &&
      !input.hpTooLow &&
      !isBlockingRefusal(input.refusal) &&
      (input.availability === "free" || input.availability === "unknown"),
    busy: input.busy,
    hintKey: attackHint(input)
  }
}

/** `null` outside a development build. The dev win skips the battle, so the battle's HP gate does not apply to it. */
export const devWinEnabled = (
  input: IAttackViewInput,
  isDevBuild: boolean
): boolean | null => {
  if (!isDevBuild) {
    return null
  }
  return (
    !input.isMoving &&
    input.onCardCell &&
    !input.tappedMobFighting &&
    !input.busy &&
    (!isBlockingRefusal(input.refusal) ||
      input.refusal === BATTLE_DENIAL_REASON.HP_TOO_LOW) &&
    (input.availability === "free" || input.availability === "unknown")
  )
}

export const isSameCell = (
  a: IStandingCell | null,
  b: IStandingCell | null
): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.mapId === b.mapId &&
    a.x === b.x &&
    a.y === b.y)

export const refusalAfterChange = (
  refusal: IAttackRefusal | null,
  prev: IRefusalContext,
  next: IRefusalContext
): IAttackRefusal | null => {
  if (refusal === BATTLE_DENIAL_REASON.HP_TOO_LOW) {
    return prev.hpTooLow && !next.hpTooLow ? null : refusal
  }
  if (refusal === MOB_ACTION_DENIAL_REASON.NO_FREE_MOB) {
    return prev.availability !== "free" && next.availability === "free"
      ? null
      : refusal
  }
  if (
    refusal !== MOB_ACTION_DENIAL_REASON.PLAYER_MOVING &&
    refusal !== MOB_ACTION_DENIAL_REASON.NOT_ON_CELL
  ) {
    return refusal
  }
  const changed =
    prev.isMoving !== next.isMoving || !isSameCell(prev.standing, next.standing)
  return changed ? null : refusal
}

export const afterEngagementOpen = (
  open: IBattleView | null,
  isDevBuild: boolean
): IEngagementOpenStep => {
  if (open !== null && open.state === BATTLE_STATE.OPEN) {
    return "GATE"
  }
  return isDevBuild ? "STALE_FIGHT" : "FAILED"
}
